import centroid from '@turf/centroid'
import type { Feature, FeatureCollection } from 'geojson'
import { omit } from 'lodash-es'
import type { Document } from 'mongodb'
import { startTransition, useEffect, useMemo, useRef } from 'react'
import { useListingsQuery } from '~/api'
import {
  aDrawnFeature,
  aFiltersFind,
  aFiltersSort,
  aHiddenListings,
  aIsMapAutoRepositioning,
  aMapCurrentCount,
  aMapFilters,
  aMapRef,
  aMapViewState,
  aMapZoomNextListingLoad,
  useAtom,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import {
  appMapBoundsContainsLngLat,
  appMapBoundsToGeoWithinPolygon,
  boundsLikeToAppMapBounds,
  DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS,
} from '~/components'
import { useEmbed, useMapCardSizes } from '~/hooks'
import type {
  MapListingProjection,
  MapPropertyListingProjection,
} from '~/models'
import { calculateIdealZoom } from '~/utils'
import { simplifyDrawPolygonFeature } from './draw'

export type MapOutletContextData = {
  listings: MapListingProjection[]
  isError: boolean
  isLoading: boolean
  isFetching: boolean
  fetchNextPage: () => void
  hasNextPage: boolean
}

function getDrawnFeatureLocationFind(mapDrawnFeature: Feature | null) {
  if (!mapDrawnFeature) return undefined

  return {
    $geoWithin: {
      $geometry: mapDrawnFeature.geometry,
    },
  }
}

export function useMapProperties() {
  const mapRef = useAtomValue(aMapRef)
  const mapViewState = useAtomValue(aMapViewState)
  const filtersFind = useAtomValue(aFiltersFind)
  const drawnFeature = useAtomValue(aDrawnFeature)
  const mapDrawnFeature = useMemo(
    () => simplifyDrawPolygonFeature(drawnFeature),
    [drawnFeature]
  )
  const setCurrentMapCount = useSetAtom(aMapCurrentCount)
  const hiddenListings = useAtomValue(aHiddenListings)
  const currentMapCountRef = useRef<number | null>(null)
  const { embedFind } = useEmbed()
  const { list } = useMapCardSizes()
  const [mapZoomNextListingLoad, setMapZoomNextListingLoad] = useAtom(
    aMapZoomNextListingLoad
  )
  const setIsMapAutoRepositioning = useSetAtom(aIsMapAutoRepositioning)
  const autoRepositionFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )

  useEffect(() => {
    return () => {
      if (autoRepositionFallbackRef.current) {
        clearTimeout(autoRepositionFallbackRef.current)
      }
      setIsMapAutoRepositioning(false)
    }
  }, [setIsMapAutoRepositioning])

  const {
    listings: properties,
    count: totalCount,
    // isError,
    isLoading: isLoadingProperties,
    isFetching: isFetchingProperties,
  } = useListingsQuery<MapPropertyListingProjection>({
    key: [
      'map-properties',
      'disable-cache',
      JSON.stringify(filtersFind),
      // hiddenListings.toString(),
    ],
    find: {
      ...filtersFind,
      ...embedFind,
      _id: { $nin: hiddenListings },
    },
    project: {
      slug: 1,
      name: 1,
      // 'property.address': 1,
      // 'property.address2': 1,
      property: 1,
      location: 1,
      type: 1,
      status: 1,
      propertyTypes: 1,
      image: { $first: '$images' },
      isBusinessListing: 1,
      locationVisibility: 1,
    },
  })
  // useEffect(() => {
  //   if (drawnFeature) {
  //     setCheckedListings(drawnProperties.map(({ _id }) => _id))
  //   }
  // }, [drawnProperties, drawnFeature, setCheckedListings])

  useEffect(() => {
    if (mapDrawnFeature) {
      return
    }

    if (!mapViewState || isLoadingProperties) {
      return
    }

    const nextMapCount = properties.filter(
      (p) =>
        !hiddenListings.includes(p._id) &&
        appMapBoundsContainsLngLat(
          mapViewState.bounds,
          p.location.coordinates as [number, number]
        )
    ).length

    if (currentMapCountRef.current === nextMapCount) {
      return
    }

    currentMapCountRef.current = nextMapCount
    startTransition(() => {
      setCurrentMapCount(nextMapCount)
    })
  }, [
    mapViewState,
    mapDrawnFeature,
    properties,
    hiddenListings,
    isLoadingProperties,
    setCurrentMapCount,
  ])

  useEffect(() => {
    if (!mapRef) {
      return
    }
    if (!isLoadingProperties) {
      const bounds = boundsLikeToAppMapBounds(mapRef.getBounds())
      const visibleProperties = properties.filter(
        (p) =>
          !hiddenListings.includes(p._id) &&
          Boolean(
            bounds &&
              appMapBoundsContainsLngLat(
                bounds,
                p.location.coordinates as [number, number]
              )
          )
      ).length
      if (
        properties.length > 0 &&
        (visibleProperties === 0 || mapZoomNextListingLoad)
      ) {
        if (mapZoomNextListingLoad) {
          setMapZoomNextListingLoad(false)
        }
        const geo: FeatureCollection = {
          type: 'FeatureCollection',
          features: properties.map((l) => {
            return {
              type: 'Feature',
              geometry: {
                ...l.location,
                type: 'Point',
              },
              properties: {},
            }
          }),
        }
        const cent = centroid(geo)
        const zoom = calculateIdealZoom(mapRef, geo.features)
        if (autoRepositionFallbackRef.current) {
          clearTimeout(autoRepositionFallbackRef.current)
        }
        setIsMapAutoRepositioning(true)
        autoRepositionFallbackRef.current = setTimeout(() => {
          setIsMapAutoRepositioning(false)
          autoRepositionFallbackRef.current = null
        }, DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS + 250)
        mapRef.easeTo({
          center: cent.geometry.coordinates as [number, number],
          duration: DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS,
          padding: { left: list, top: 0, right: 0, bottom: 0 },
          zoom: zoom > 16 ? 16 : zoom,
        })
      }
    }
  }, [isLoadingProperties])

  return {
    properties: properties.filter((p) => !hiddenListings.includes(p._id)),
    totalCount,
    isLoadingProperties,
    isFetchingProperties,
  }
}

export function useMapListings<
  TListing extends { _id: string } = MapListingProjection,
>({
  limit = 60,
  offset,
  project,
  useMapSearch = true,
}: {
  limit?: number
  offset?: number
  project?: Document
  useMapSearch?: boolean
}) {
  const mapViewState = useAtomValue(aMapViewState)
  const mapFilters = useAtomValue(aMapFilters)
  const filtersFind = useAtomValue(aFiltersFind)
  const filtersSort = useAtomValue(aFiltersSort)
  const drawnFeature = useAtomValue(aDrawnFeature)
  const mapDrawnFeature = useMemo(
    () => simplifyDrawPolygonFeature(drawnFeature),
    [drawnFeature]
  )
  const hiddenListings = useAtomValue(aHiddenListings)
  const { embedFind } = useEmbed()
  const queryEnabled = !useMapSearch || Boolean(mapViewState) || !!mapDrawnFeature
  const mapFilterKey = useMemo(
    () => JSON.stringify(omit(mapFilters, ['lat', 'lng', 'z', 'b', 'p'])),
    [mapFilters]
  )
  const shouldProjectTransactions = Boolean(
    mapFilters.statuses?.some((status) =>
      ['leased', 'sold'].includes(status)
    )
  )

  const { listingsFind, listingFindKey } = useMemo(() => {
    if (useMapSearch && !mapViewState && !mapDrawnFeature) {
      return {}
    }
    const find: { [key: string]: any } = {
      ...filtersFind,
      ...embedFind,
      _id: { $nin: hiddenListings },
    }

    const drawnFeatureLocationFind =
      getDrawnFeatureLocationFind(mapDrawnFeature)

    if (drawnFeatureLocationFind) {
      find.location = drawnFeatureLocationFind
    } else if (useMapSearch && mapViewState) {
      find.location = {
        $geoWithin: {
          $geometry: appMapBoundsToGeoWithinPolygon(mapViewState.bounds),
        },
      }
    }

    return { listingsFind: find, listingFindKey: omit(find, ['_id']) }
  }, [
    mapViewState?.bounds,
    useMapSearch,
    filtersFind,
    embedFind,
    mapDrawnFeature,
    hiddenListings,
  ])

  const {
    listings,
    isError,
    isLoading,
    isFetching,
    refetch,
    fetchNextPage,
    hasNextPage,
    count,
  } = useListingsQuery<TListing>({
    key: [
      'map-listings',
      mapFilterKey,
      JSON.stringify({ ...listingFindKey, ...filtersSort }),
      ...(offset === undefined ? [] : [offset.toString()]),
    ],
    find: { status: 'active', ...listingsFind },
    sort: filtersSort,
    project: project ?? {
      location: 1,
      created: 1,
      activeDate: 1,
      modified: 1,
      coreModified: 1,
      name: 1,
      slug: 1,
      property: 1,
      type: 1,
      status: 1,
      propertyTypes: 1,
      'meta.lotSizeMin': 1,
      'meta.lotSizeMax': 1,
      'meta.lotSizeUnit': 1,
      'meta.price': 1,
      'meta.priceMax': 1,
      'meta.priceType': 1,
      'meta.priceNegotiable': 1,
      'meta.totalUnits': 1,
      filters: 1,
      images: { $slice: 1 },
      company: 1,
      brokers: 1,
      leaseSpaces: 1,
      ...(shouldProjectTransactions
        ? {
            'transactions._id': 1,
            'transactions.date': 1,
            'transactions.confidential': 1,
            'transactions.propertyType': 1,
            'transactions.lease.rate': 1,
            'transactions.lease.rateType': 1,
            'transactions.lease.footage': 1,
            'transactions.lease.acres': 1,
            'transactions.lease.space': 1,
            'transactions.listingBrokers': 1,
            'transactions.sale.price': 1,
            'transactions.sale.buildingSf': 1,
            'transactions.sale.acres': 1,
          }
        : {}),
      isBusinessListing: 1,
      locationVisibility: 1,
      'meta.virtualTour': 1,
    },
    limit,
    offset,
    enabled: queryEnabled,
  })

  const visibleListings = useMemo(() => {
    return listings.filter((l) => hiddenListings.includes(l._id) === false)
  }, [listings, hiddenListings])

  return {
    listings: visibleListings,
    isError,
    isLoading,
    isFetching,
    refetch,
    fetchNextPage,
    hasNextPage,
    count,
  }
}
